import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Save } from 'lucide-react';
import AdminDoctorService from '../../services/admin.doctor.service';
import PropTypes from 'prop-types';

const AdminDoctorServices = ({ therapistId }) => {
    const [services, setServices] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [form, setForm] = useState({
        name: '',
        price: '',
        duration: '',
        description: ''
    });

    useEffect(() => {
        if (therapistId) {
            loadServices();
        }
    }, [therapistId]);

    const loadServices = async () => {
        if (!therapistId) return;
        try {
            const data = await AdminDoctorService.getServices(therapistId);
            data.map(service => ({
                ...service,
                price: Number(service.price),
                duration: Number(service.duration)
            }));
            setServices(data);
        } catch (error) {
            console.error('[DEBUG] Error loading services:', error);
        }
    };

    const handleAddDefaultServices = async () => {
        // Aggiungiamo una conferma prima di procedere
        const confirmed = window.confirm("Stiamo aggiungendo nuovi servizi al professionista. Sei sicuro?");
        
        if (!confirmed) {
            return; // Se l'utente annulla, interrompiamo l'operazione
        }
        
        try {
            await AdminDoctorService.addDefaultServices(therapistId);
            loadServices(); // Ricarica i servizi dopo l'aggiunta
        } catch (error) {
            console.error('[DEBUG] Error adding default services:', error);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingService) {
                await AdminDoctorService.updateService(therapistId, editingService.id, form);
            } else {
                await AdminDoctorService.createService(therapistId, {
                    ...form,
                    therapist_id: therapistId
                });
            }
            setShowModal(false);
            loadServices();
            resetForm();
        } catch (error) {
            if (error.errors) {
                const errorMessages = Object.values(error.errors).flat().join('\n');
                alert(errorMessages);
            } else {
                alert('Errore nel salvataggio');
            }
        }
    };

    const resetForm = () => {
        setForm({ name: '', price: '', duration: '', description: '' });
        setEditingService(null);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Servizi del Professionista</h2>
                <div className="flex gap-2">
                    <button
                        onClick={handleAddDefaultServices}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Aggiungi Servizi Default
                    </button>
                    <button 
                        onClick={() => setShowModal(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                        Nuovo Servizio
                    </button>
                </div>
            </div>

            <div className="grid gap-4">
                {services.length > 0 ? (
                    services.map(service => (
                        <ServiceCard 
                            key={service.id}
                            service={service}
                            onEdit={() => {
                                setForm(service);
                                setEditingService(service);
                                setShowModal(true);
                            }}
                            onDelete={async () => {
                                await AdminDoctorService.deleteService(therapistId, service.id);
                                loadServices();
                            }}
                        />
                    ))
                ) : (
                    <div className="text-center p-4 text-gray-500">
                        Nessun Servizio assegnato al professionista
                    </div>
                )}
            </div>

            <ServiceModal 
                show={showModal}
                onClose={() => {
                    setShowModal(false);
                    resetForm();
                }}
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                isEditing={!!editingService}
            />
        </div>
    );
};

// Componente ServiceCard per visualizzare un singolo servizio
const ServiceCard = ({ service, onEdit, onDelete }) => {
    const handleDelete = () => {
        if (window.confirm('Sei sicuro di voler eliminare questo servizio?')) {
            onDelete();
        }
    };

    return (
        <div className="p-4 border rounded-lg bg-white">
            <div className="flex justify-between">
                <div>
                    <h3 className="font-semibold">{service.name}</h3>
                    <p className="text-gray-600">{service.description}</p>
                    <div className="mt-2 space-x-4">
                        <span>€{service.price}</span>
                        <span>{service.duration} min</span>
                    </div>
                </div>
                <div className="flex space-x-2">
                    <button 
                        onClick={onEdit}
                        className="text-blue-600 hover:bg-blue-50 p-2 rounded-full"
                    >
                        <Edit className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={handleDelete}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-full"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Componente modale per creare/modificare servizi
const ServiceModal = ({ show, onClose, form, setForm, onSubmit, isEditing }) => {
    const [error, setError] = useState('');
    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price || !form.duration) {
            setError('Tutti i campi sono obbligatori');
            return;
        }
        try {
            await onSubmit(e);
            setError('');
        } catch (error) {
            setError('Errore nel salvataggio del servizio');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h2 className="text-lg font-semibold mb-4">
                    {isEditing ? 'Modifica Servizio' : 'Nuovo Servizio'}
                </h2>

                {error && (
                    <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        placeholder="Nome servizio"
                        value={form.name}
                        onChange={e => setForm({...form, name: e.target.value})}
                        className="w-full p-2 border rounded"
                    />
                    <input
                        type="number"
                        placeholder="Prezzo €"
                        value={form.price}
                        onChange={e => setForm({...form, price: e.target.value})}
                        className="w-full p-2 border rounded"
                    />
                    <input
                        type="number"
                        placeholder="Durata (minuti)"
                        value={form.duration}
                        onChange={e => setForm({...form, duration: e.target.value})}
                        className="w-full p-2 border rounded"
                    />
                    <textarea
                        placeholder="Descrizione"
                        value={form.description}
                        onChange={e => setForm({...form, description: e.target.value})}
                        className="w-full p-2 border rounded"
                        rows={3}
                    />
                    <div className="flex justify-end space-x-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                            Annulla
                        </button>
                        <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded">
                            {isEditing ? 'Aggiorna' : 'Crea'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

AdminDoctorServices.propTypes = {
    therapistId: PropTypes.number
};

ServiceCard.propTypes = {
    service: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        price: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]).isRequired,
        duration: PropTypes.oneOfType([
            PropTypes.number, 
            PropTypes.string
        ]).isRequired
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

ServiceModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    form: PropTypes.shape({
        name: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
        duration: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired
    }).isRequired,
    setForm: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isEditing: PropTypes.bool.isRequired
};

export default AdminDoctorServices;